import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CloseOutlined } from '@ant-design/icons';
import { Button, Flex, Form, Input, Select, Space, Typography } from 'antd';
import { setSelectedStep, setWorkflow } from '../../../redux/slices/connectorSlice';
import { setMasterList } from '../../../redux/slices/masterDataSlice';
import { getPreviousFormsForConditions } from '../../../utils/workflowHelper';
import { getAllWidgets } from '../../../utils/formsHelper';
import { getMasterList } from '../../../services/adminServices';
import SvgIcons from '../../layout/assets/SvgIcons';
import { trimStringValues } from '../../../utils/trimStrings';
import { useTranslation } from 'react-i18next';

const DEFAULT_PLACES_INFO = {
  formFields: [],
  masterColumns: [],
  masterToBeUpdated: ''
};

const hiddenWidgetTypes = ['imageForm', 'infoForm', 'linkField', 'switchForm', 'divider', 'section', 'attachment'];

const GooglePlacesPanel = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { selectedStep, workflow, workflowForms } = useSelector(s => s.connector);
  const { masterList } = useSelector(s => s.masterData);
  const [form] = Form.useForm();
  const [formItems, setFormItems] = useState([]);
  const [masterColumns, setMasterColumns] = useState([]);
  const formValues = Form.useWatch([], form);

  const masterOptions = (masterList || []).map(master => ({
    label: master.dataName,
    value: master.dataId,
  }));

  const isGooglePlacesStep = !!(selectedStep?.id && (selectedStep?.data?.type === 'googlePlaces' || selectedStep?.type === 'googlePlaces'));
  const nodeId = selectedStep?.id;

  useEffect(() => {
    const el = document.getElementById('google-places-panel');
    if (el) el.style.display = isGooglePlacesStep ? 'block' : 'none';
  }, [isGooglePlacesStep]);

  

  useEffect(() => {
    if (!isGooglePlacesStep || !nodeId) return;
    const node = (workflow.nodes || []).find(n => n.id === nodeId);
    const nodeData = node?.data || {};
    const placesInfo = { ...DEFAULT_PLACES_INFO, ...(nodeData?.googlePlacesInfo || {}) };
    form.setFieldsValue({
      name: nodeData?.name || 'Google Places',
      formFields: placesInfo.formFields || undefined,
      masterToBeUpdated: placesInfo.masterToBeUpdated || undefined,
      masterColumns: placesInfo.masterColumns || undefined, 
    });
  }, [isGooglePlacesStep, nodeId, workflow.nodes, form]);

  useEffect(() => {
    if (!isGooglePlacesStep || (masterList && masterList.length > 0)) return;
    const fetchMasterList = async () => {
      const response = await getMasterList();
      dispatch(setMasterList(Array.isArray(response?.data) ? response.data : []));
    };
    fetchMasterList();
  }, [isGooglePlacesStep, masterList, dispatch]);

  useEffect(() => {
    if (formValues?.masterToBeUpdated) {
      const selectedMaster = masterList.find(master => master.dataId === formValues.masterToBeUpdated);
      if (selectedMaster) {
        const columns = (selectedMaster?.fields || []).map(col => ({
          label: col.label,
          value: col.id,
        }));
        setMasterColumns(columns);
      }
    }
  }, [formValues]);

  useEffect(() => {
    if (workflowForms?.length > 0 && workflow.nodes && isGooglePlacesStep) {
      const previousNodes = getPreviousFormsForConditions(workflow.nodes, workflow.edges, selectedStep);
      const previousFormIds = new Set();
      const finalForms = [];

      previousNodes.forEach(node => {
        if (node?.formData?.formId && !previousFormIds.has(node.formData.formId)) {
          const nodeForm = workflowForms.find(formData => formData.formId === node.formData.formId);
          if (nodeForm) {
            previousFormIds.add(node.formData.formId);
            finalForms.push(nodeForm);
          }
        }
      });

      let elements = [];
      finalForms.forEach(curForm => {
        const allWidgets = getAllWidgets(curForm.items || []);
        elements = elements.concat(allWidgets);
      });

      elements = elements
        .filter(el => !hiddenWidgetTypes.includes(el.formType))
        .map(element => ({ label: element.label, value: element.id }));

      setFormItems(elements);
    }
  }, [workflowForms, isGooglePlacesStep, workflow.edges, workflow.nodes, selectedStep]);

  const onClose = () => dispatch(setSelectedStep({}));

  const onSave = async () => {
    const values = trimStringValues(await form.validateFields());

    const nextNodes = (workflow.nodes || []).map(node => {
      if (node.id !== nodeId) return { ...node };
      return {
        ...node,
        data: {
          ...node.data,
          type: 'googlePlaces',
          name: values.name || 'Google Places',
          googlePlacesInfo: {
            ...DEFAULT_PLACES_INFO,
            formFields: values.formFields || [],
            masterToBeUpdated: values.masterToBeUpdated || '',
            masterColumns: values.masterColumns || [],

          },
        },
      };
    });

    dispatch(setWorkflow({ ...workflow, nodes: nextNodes }));
    onClose();
  };

  if (!isGooglePlacesStep) return null;

  return (
    <div className="step-details-container" id="google-places-panel">
      <Flex className='step-details-header' align='center' justify='space-between'>
        <Typography.Title level={5} className='margin-0'>
          <Space>
            <SvgIcons type='location' className='w-4 h-4' />
            {t('Google Places')}
          </Space>
        </Typography.Title>
        <CloseOutlined className='icon-medium' onClick={onClose} />
      </Flex>

      <div className='step-details-body'>
        <Form form={form} layout='vertical' onFinish={onSave}>
          <Form.Item label={t('Name')} name='name' rules={[{ required: true, whitespace: true, message: 'Name is required' }]}>
            <Input disabled />
          </Form.Item>

          <div className='mb-3 text-sm text-gray-600'>{t('Map brand and city fields. The fetched store locations will show in the request workflow panel.')}</div>

          <Form.Item label={t('Form fields to be used in the google places query')} name='formFields' rules={[{ required: true, message: 'Select form fields to be used in google places search' }]}>
            <Select allowClear mode="multiple" showSearch optionFilterProp='label' options={formItems} placeholder={t('Select form fields')} />
          </Form.Item>

          <Form.Item label={t('Master to be updated')} name='masterToBeUpdated'>
            <Select allowClear showSearch optionFilterProp='label' options={masterOptions} placeholder={t('Select master to update')} />
          </Form.Item>

          <Form.Item label={t('Master Columns to be updated')} name='masterColumns'>
            <Select mode="multiple" allowClear showSearch optionFilterProp='label' options={masterColumns} placeholder={t('Select master columns to update')} />
          </Form.Item>

          <Form.Item className='step-details-action'>
            <Flex gap={12}>
              <Button type='primary' htmlType='submit' className='submit-button' style={{ width: '9.75rem' }}>{t('Save')}</Button>
              <Button onClick={onClose} style={{ width: '9.75rem' }}>{t('Cancel')}</Button>
            </Flex>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default GooglePlacesPanel;